import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { contactPageSchema } from "@/lib/seo";
import GeneralInquiryForm from "./components/GeneralInquiryForm";
import CorporateForm from "./components/CorporateForm";
import SpeakerForm from "./components/SpeakerForm";

const REQUEST_TYPES = [
  {
    icon: "ri-user-heart-line",
    title: "個人成長／課程",
    desc: "想了解適合自己的成長方案或課程。",
    cta: "詢問課程與成長方案",
    href: "#general-form",
  },
  {
    icon: "ri-team-line",
    title: "企業與團隊培訓",
    desc: "為企業或團隊安排培訓，提升執行力與人才發展。",
    cta: "提出企業培訓需求",
    href: "#corporate-form",
  },
  {
    icon: "ri-megaphone-line",
    title: "講師／演講邀約",
    desc: "邀請擔任講師、演講或工作坊引導。",
    cta: "提出講師邀約",
    href: "#speaker-form",
  },
  {
    icon: "ri-hand-heart-line",
    title: "合作洽談",
    desc: "其他合作方式，歡迎與我們聊聊。",
    cta: "洽談合作",
    href: "#general-form",
  },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "Email", value: "待補" },
  { icon: "ri-line-line", label: "LINE", value: "待補" },
  { icon: "ri-phone-line", label: "電話", value: "待補" },
  { icon: "ri-facebook-line", label: "Facebook", value: "待補" },
  { icon: "ri-instagram-line", label: "Instagram", value: "待補" },
  { icon: "ri-youtube-line", label: "YouTube", value: "待補" },
];

export default function Contact() {
  const { openAi } = useUi();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="聯絡許文彰｜企業培訓・講師邀約・課程合作｜幸福人才"
        description="聯絡許文彰，無論是個人成長課程、企業培訓、講師邀約或合作洽談，先告訴我們你現在真正希望解決的問題。"
        keywords="聯絡,合作,企業培訓,講師邀約,課程,許文彰"
        jsonLd={contactPageSchema}
      />
      <PageHero
        eyebrow="聯絡與合作"
        title={
          <>
            有些合作，
            <br />
            可以從先把需求說清楚開始。
          </>
        }
        description={
          <>
            <p>
              不論你是正在尋找適合自己的成長方案、希望為企業或團隊安排培訓、
              正在規畫講座或活動，或想討論其他合作方式，都可以先告訴我們：
              你現在真正希望解決什麼問題。
            </p>
          </>
        }
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() =>
              document.getElementById("request-types")?.scrollIntoView({ behavior: "smooth" })
            }
            icon="ri-arrow-down-line"
          >
            選擇我的需求
          </Button>
          <Button
            onClick={() =>
              document.getElementById("line")?.scrollIntoView({ behavior: "smooth" })
            }
            variant="outline"
            iconLeft
            icon="ri-line-line"
          >
            加入 LINE 聯絡
          </Button>
        </div>
      </PageHero>

      {/* 需求分流 */}
      <section id="request-types" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="需求分流" title="先告訴我們，你需要的幫助。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REQUEST_TYPES.map((r, i) => (
              <Reveal key={r.title} delay={i * 70}>
                <div className="h-full flex flex-col rounded-card bg-cream border border-line p-7 transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-gold-soft/70">
                    <i className={`${r.icon} text-2xl text-primary`} aria-hidden="true"></i>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-ink">{r.title}</h3>
                  <p className="mt-2 text-sm leading-[1.8] text-ink-soft">{r.desc}</p>
                  <div className="mt-6 pt-5 border-t border-line mt-auto">
                    <a
                      href={r.href}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-gold transition-colors cursor-pointer"
                    >
                      {r.cta}
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 一般詢問 Form */}
      <section id="general-form" className="bg-cream scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionHeading
                  eyebrow="一般詢問"
                  title="有想了解的事，歡迎告訴我們。"
                />
                <p className="mt-5 text-base leading-[1.85] text-ink-soft">
                  關於個人成長、課程或其他問題，都可以透過下方表單聯繫。
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={80}>
                <div className="rounded-card bg-white border border-line p-7 md:p-9">
                  <GeneralInquiryForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 企業培訓需求 Form */}
      <section id="corporate-form" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionHeading
                  eyebrow="企業培訓需求"
                  title="為你的團隊，找到真正能落地的培訓。"
                />
                <p className="mt-5 text-base leading-[1.85] text-ink-soft">
                  留下你的需求，我們會依問題、對象、形式與時間，協助規畫適合的培訓方案。
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={80}>
                <div className="rounded-card bg-cream border border-line p-7 md:p-9">
                  <CorporateForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 講師邀約 Form */}
      <section id="speaker-form" className="bg-cream scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionHeading eyebrow="講師邀約" title="邀請許文彰，為你的活動帶來改變。" />
                <p className="mt-5 text-base leading-[1.85] text-ink-soft">
                  演講、工作坊或培訓邀約，請留下活動資訊，我們會進一步確認時間與主題。
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={80}>
                <div className="rounded-card bg-white border border-line p-7 md:p-9">
                  <SpeakerForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LINE */}
      <section id="line" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <SectionHeading
                eyebrow="LINE"
                title="想先簡單詢問，可以從 LINE 開始。"
                align="center"
              />
              <div className="mt-8 rounded-card bg-cream border border-line p-8">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-md bg-gold-soft/70">
                  <i className="ri-line-line text-2xl text-primary" aria-hidden="true"></i>
                </div>
                <p className="mt-5 text-base font-semibold text-ink">LINE 官方帳號</p>
                <p className="mt-2 text-sm text-ink-soft">連結即將上線，敬請期待</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AI */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl md:text-[2.125rem] font-bold leading-[1.35] text-white">
                不知道怎麼說明需求？
              </h2>
              <p className="mt-5 text-base leading-[1.8] text-gold-soft/90">
                先和 AI 幸福人才顧問聊聊，把想法整理清楚，再決定下一步。
              </p>
              <div className="mt-8">
                <Button onClick={openAi} size="lg" icon="ri-chat-3-line">
                  和 AI 幸福人才顧問聊聊
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 聯絡資料 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="聯絡資料" title="更多聯絡方式。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CONTACTS.map((c, i) => (
              <Reveal key={c.label} delay={i * 50}>
                <div className="rounded-card bg-cream border border-line p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-md bg-gold-soft/70">
                    <i className={`${c.icon} text-lg text-primary`} aria-hidden="true"></i>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-ink">{c.label}</p>
                  <p className="mt-1 text-xs text-ink-soft/60">{c.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <p className="mt-6 text-xs text-ink-soft/60">
              聯絡資料待確認後補上
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}